import React, { useContext, useEffect, useRef } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import KEY_CODES from 'common/KeyCodes';
import TextInput, { TextInputProps } from 'components/TextInput';
import { TreeNodeContext, TreeBranchContext } from './';
import Styled from './styles';

const { ENTER_KEY, SPACE_KEY } = KEY_CODES;

export interface TreeInputProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange' | 'onSubmit'> {
  /** 'value' of the input element -- See TextInput component for more information. */
  value: string;
  /** 'onChange' callback -- See TextInput component for more information. */
  onChange?: React.ChangeEventHandler;
  /** Callback fired when user hits Enter key to submit value. */
  onSubmit?: (arg: React.KeyboardEvent) => void;
  /** TextInput props. See TextInput component for more information. */
  textInputProps?: Partial<TextInputProps>;
  /** Allow the item text to be edited. */
  allowEdit?: boolean;
}

/** input component to display in a Tree to allow editing. Any other props will be provided to the wrapper element. */
const TreeInput = React.forwardRef<HTMLElement, TreeInputProps>((props: TreeInputProps, ref) => getContext(({ RTL, theme }) => {
  const {
    value,
    onChange,
    onSubmit,
    textInputProps,
    allowEdit,
    ...otherProps
  } = props;
  const treeNodeContext = useContext(TreeNodeContext);
  const treeBranchContext = useContext(TreeBranchContext);
  const inputRef = useRef(null);
  const { inputProps } = textInputProps;
  const combinedInputRefs = useCombinedRefs(inputProps && inputProps.ref, inputRef);

  useEffect(() => {
    if (allowEdit) {
      // focus input
      inputRef && inputRef.current && inputRef.current.focus();
    } else {
      inputRef && inputRef.current && inputRef.current.blur();
    }
  }, [allowEdit]);

  const handleKeyDown = (event) => {
    const { key } = event;
    const { onKeyDown } = textInputProps;
    if (key === ENTER_KEY) {
      // to not toggle the TreeBranch open state
      event.stopPropagation();
      onSubmit && onSubmit(event);
    }
    if (key === SPACE_KEY) {
      // to not toggle the TreeBranch open state
      event.stopPropagation();
    }
    onKeyDown && onKeyDown(event);
  };

  const handleDoubleClick = (event) => {
    // to not toggle the TreeBranch
    event.preventDefault();
    event.stopPropagation();
    const { onDoubleClick } = textInputProps;
    onDoubleClick && onDoubleClick(event);
  };

  const isError = treeNodeContext.error || treeBranchContext.error;
  let inputErrorMessage = treeNodeContext.error && treeNodeContext.errorMessage;
  if (treeBranchContext.error) {
    inputErrorMessage = treeBranchContext.errorMessage;
  }
  const isDisabled = treeNodeContext.disabled || treeBranchContext.disabled;
  const isSelected = treeNodeContext.selected || treeBranchContext.selected;
  const TEXTINPUT_PROPS = {
    variant: 'outlined',
    value,
    onChange,
    disabled: isDisabled,
    error: isError,
    errorMessage: inputErrorMessage,
    inputProps: {
      tabIndex: allowEdit ? 0 : -1,
      ref: combinedInputRefs,
    },
  };
  const resolvedTextInputProps = _merge({}, TEXTINPUT_PROPS, textInputProps);
  return (
    <Styled.TreeInputStyle
      ref={ref}
      rtl={RTL}
      $theme={theme.TREE}
      selected={isSelected}
      error={isError}
      {...otherProps}
    >
      <TextInput value={value} {...resolvedTextInputProps} onKeyDown={handleKeyDown} onDoubleClick={handleDoubleClick} />
    </Styled.TreeInputStyle>
  );
}));

TreeInput.displayName = 'TreeInput';

TreeInput.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,
  textInputProps: {},
  allowEdit: false,
};

export { TreeInput as default };
